import { useEffect, useState, type FormEvent } from 'react';
import { Loader2, MessageCircleQuestion, Sparkles, X } from 'lucide-react';
import { CategoriaExigencia, SituacaoLeitura, type EditalDto, type OrigemDto, type RespostaPerguntaDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import type { AlvoLicitacao } from '../../application/portas';

const CATEGORIAS: Record<CategoriaExigencia, string> = {
  [CategoriaExigencia.HabilitacaoJuridica]: 'Habilitação jurídica',
  [CategoriaExigencia.RegularidadeFiscal]: 'Regularidade fiscal e trabalhista',
  [CategoriaExigencia.EconomicoFinanceira]: 'Econômico-financeira',
  [CategoriaExigencia.QualificacaoTecnica]: 'Qualificação técnica',
  [CategoriaExigencia.Declaracoes]: 'Declarações',
  [CategoriaExigencia.Proposta]: 'Proposta',
  [CategoriaExigencia.Amostra]: 'Amostra',
  [CategoriaExigencia.VisitaTecnica]: 'Visita técnica',
  [CategoriaExigencia.Outras]: 'Outras',
};
const moeda = (v: number | null) => (v === null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const dataHora = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'sem data');

/** Origem da informação: documento e página; o trecho literal aparece ao passar o mouse. */
function Origem({ o }: { o: OrigemDto }) {
  if (!o.pagina) return null;
  return (
    <span className="edital__origem" title={o.trecho ? `“${o.trecho}”\n${o.documento ?? ''}` : (o.documento ?? '')}>
      p. {o.pagina}
    </span>
  );
}

/** Lê o edital do processo e repete enquanto a leitura não termina. */
function useEdital(alvo: AlvoLicitacao) {
  const { apiLicitacao } = useDependencias();
  const [edital, definir] = useState<EditalDto | null | 'carregando'>('carregando');
  useEffect(() => {
    let ativo = true;
    let relogio: ReturnType<typeof setTimeout> | undefined;
    const ler = async () => {
      const e = await apiLicitacao.obterEdital(alvo).catch(() => null);
      if (!ativo) return;
      definir(e);
      if (e && e.situacao !== SituacaoLeitura.Concluido && e.situacao !== SituacaoLeitura.Falhou) relogio = setTimeout(() => void ler(), 5000);
    };
    void ler();
    return () => {
      ativo = false;
      clearTimeout(relogio);
    };
  }, [apiLicitacao, alvo]);
  return edital;
}

/** Edital lido pela IA dentro da licitação (ADR-0019): tudo com a origem e perguntas com citação. */
export function PainelEdital({ alvo, fechar }: { alvo: AlvoLicitacao; fechar: () => void }) {
  const edital = useEdital(alvo);
  return (
    <aside className="edital" aria-label="Edital lido pela IA">
      <header className="edital__cabecalho">
        <h2>
          <Sparkles size={18} aria-hidden /> Edital
        </h2>
        <button type="button" className="botao-icone" onClick={fechar} aria-label="Fechar o painel do edital">
          <X size={18} />
        </button>
      </header>
      {edital === 'carregando' && <p className="edital__vazio">Carregando…</p>}
      {edital === null && <p className="edital__vazio">Esta licitação ainda não tem edital lido. Use “Enviar arquivos” com os arquivos do edital para o Nexital ler e organizar.</p>}
      {edital && edital !== 'carregando' && <ConteudoEdital edital={edital} alvo={alvo} />}
    </aside>
  );
}

function ConteudoEdital({ edital, alvo }: { edital: EditalDto; alvo: AlvoLicitacao }) {
  if (edital.situacao !== SituacaoLeitura.Concluido) {
    return (
      <p className="edital__vazio">
        <Loader2 size={16} className="girando" aria-hidden /> Lendo e analisando os documentos…
      </p>
    );
  }
  const categorias = [...new Set(edital.exigencias.map((e) => e.categoria))];
  return (
    <div className="edital__corpo">
      <Perguntas alvo={alvo} />
      {edital.resumo && <p className="edital__resumo">{edital.resumo}</p>}
      <section>
        <h3>Ficha</h3>
        <dl className="edital__ficha">
          {edital.campos.map((c) => (
            <div key={c.campo}>
              <dt>{c.rotulo}</dt>
              <dd>
                {c.valor} <Origem o={c} /> {!c.confirmado && <span className="pilula pilula--aguardando">a conferir</span>}
              </dd>
            </div>
          ))}
        </dl>
      </section>
      {edital.prazos.length > 0 && (
        <section>
          <h3>Prazos</h3>
          <ul className="edital__lista">
            {edital.prazos.map((p, i) => (
              <li key={i}>
                <strong>{dataHora(p.data)}</strong> {p.descricao} <Origem o={p} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {edital.itens.length > 0 && <Itens edital={edital} />}
      {categorias.map((cat) => (
        <section key={cat}>
          <h3>{CATEGORIAS[cat]}</h3>
          <ul className="edital__lista">
            {edital.exigencias.filter((e) => e.categoria === cat).map((e, i) => (
              <li key={i}>
                {e.descricao} <Origem o={e} />
              </li>
            ))}
          </ul>
        </section>
      ))}
      <Documentos edital={edital} />
    </div>
  );
}

function Itens({ edital }: { edital: EditalDto }) {
  return (
    <section>
      <h3>Itens ({edital.itens.length})</h3>
      <table className="edital__itens">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qtd.</th>
            <th>Descrição</th>
            <th>Ref. unit.</th>
          </tr>
        </thead>
        <tbody>
          {edital.itens.map((i, n) => (
            <tr key={n}>
              <td>{[i.lote && `L${i.lote}`, i.numero].filter(Boolean).join(' · ') || n + 1}</td>
              <td>{i.quantidade ?? '—'} {i.unidade}</td>
              <td>
                {i.descricao} <Origem o={i} />
                {i.descricaoImagem && <small className="edital__imagem">Imagem: {i.descricaoImagem}</small>}
              </td>
              <td>{moeda(i.valorUnitario)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function Documentos({ edital }: { edital: EditalDto }) {
  return (
    <section>
      <h3>Documentos lidos</h3>
      <ul className="edital__lista">
        {edital.documentos.map((d) => (
          <li key={d.uuid}>
            <strong>{d.nome}</strong> {d.paginas ? `· ${d.paginas} páginas` : ''}
            {d.secoes.length > 1 && (
              <ul>
                {d.secoes.map((s) => (
                  <li key={s.paginaInicial}>
                    {s.titulo} <small>p. {s.paginaInicial}–{s.paginaFinal}</small>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Pergunte ao edital: resposta só com base nos documentos, com citação; sem base, a frase padrão. */
function Perguntas({ alvo }: { alvo: AlvoLicitacao }) {
  const { apiLicitacao } = useDependencias();
  const [pergunta, definirPergunta] = useState('');
  const [resposta, definirResposta] = useState<RespostaPerguntaDto | null>(null);
  const [carregando, definirCarregando] = useState(false);
  const [erro, definirErro] = useState<string>();
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    definirCarregando(true);
    definirErro(undefined);
    try {
      definirResposta(await apiLicitacao.perguntar(alvo, pergunta));
    } catch (x) {
      definirErro((x as Error).message);
    } finally {
      definirCarregando(false);
    }
  };
  return (
    <form className="edital__pergunta" onSubmit={enviar}>
      <label>
        <MessageCircleQuestion size={18} aria-hidden />
        <input value={pergunta} onChange={(e) => definirPergunta(e.target.value)} placeholder="Pergunte ao edital (ex.: precisa de atestado?)" aria-label="Pergunta ao edital" />
      </label>
      <button type="submit" className="botao botao--primario" disabled={carregando || pergunta.trim().length < 3}>
        {carregando ? <Loader2 size={16} className="girando" aria-hidden /> : 'Perguntar'}
      </button>
      {erro && <p className="cartao-acesso__erro">{erro}</p>}
      {resposta && (
        <div className="edital__resposta" aria-live="polite">
          {resposta.resposta && <p>{resposta.resposta}</p>}
          {resposta.resposta === null && <p>Trechos do edital mais ligados à pergunta:</p>}
          {resposta.citacoes.map((c, i) => (
            <blockquote key={i}>
              “{c.trecho}” <cite>{c.documento}, p. {c.pagina}</cite>
            </blockquote>
          ))}
        </div>
      )}
    </form>
  );
}
